import {
  AutoComplete,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  TimePicker,
  Upload,
} from "antd";
import { MaskedInput } from "antd-mask-input";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, RegisterOptions } from "react-hook-form";
import { default as RSelect } from "react-select";
import CreatableSelect from "react-select/creatable";
import { DATE_FORMATS } from "../../libs/utils";
import { translateService } from "../../modules/BookingForm/ducks/services";
import { renderExtra } from "./renderExtra";

const { RangePicker } = DatePicker;

export const InputField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    tooltip,
    helpingText,
    id,
    formItemStyle,
    inputStyle,
    valueAsNumber,
    addonBefore,
  } = props;

  const [fieldRules, setFieldRules] = useState<RegisterOptions>({});
  const [valueGot, setValueGot] = useState(null);

  useEffect(() => {
    setValueGot(props?.valueGot);
  }, [props?.valueGot]);

  useEffect(() => {
    valueGot != null && valueGot != undefined && valueGot != false && props.setValue(fieldname, valueGot);
    if (valueGot == "empty") {
      props.setValue(fieldname, "");
    } else if (valueGot == 0) {
      props.setValue(fieldname, null);
    }
  }, [valueGot]);

  useEffect(() => {
    if (props.disabled == true) {
      setFieldRules({});
      if (props?.register) props?.register(fieldname, { required: false });
      if (props?.resetField && !valueGot) props?.resetField(fieldname);
    } else {
      setFieldRules(rules);
    }
  }, [props.disabled, isRequired]);

  const effectiveRules = rules && Object.keys(rules).length > 0 ? rules : fieldRules;
  const extendedFieldRules: RegisterOptions = {
    ...effectiveRules,
    ...(effectiveRules?.pattern
      ? {}
      : {
          pattern: {
            value: /^\S.*$/,
            message: "Spaces are not allowed",
          },
        }),
  };

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage || undefined}
        className={props.classes}
        tooltip={tooltip}
        style={formItemStyle}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={isRequired ? extendedFieldRules : effectiveRules}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Input
              value={value ?? ""}
              ref={ref}
              addonBefore={addonBefore || undefined}
              onChange={(e) => {
                const value = e?.target?.value;
                const formattedValue = valueAsNumber ? Number(value) : value;
                const modifiedEvent = { ...e, target: { ...e.target, value: formattedValue } };
                onChange(modifiedEvent); // send value to hook form
                props.onChange && props.onChange(modifiedEvent, null);
              }}
              id={id}
              onBlur={props.onBlur}
              disabled={props.disabled}
              style={inputStyle}
              onKeyDown={(e) => {
                if (iProps?.type === "number") {
                  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab"];
                  const isDigit = /^[0-9]$/.test(e.key);

                  // ✅ If max is defined and value already >= max, block further input
                  if (
                    iProps?.max !== undefined &&
                    value !== null &&
                    value !== "" &&
                    Number(value) >= Number(iProps.max) &&
                    !allowedKeys.includes(e.key)
                  ) {
                    e.preventDefault();
                    return;
                  }

                  // ✅ Normal number input rules
                  if (!isDigit && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }
              }}
              {...iProps}
            />
          )}
        />
        {helpingText ? renderExtra(helpingText) : null}
      </Form.Item>
    </>
  );
};

export const InputAutocompleteField = React.memo((props: any) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    tooltip,
    options,
    style,
    filterOption,
    addonBefore,
    value,
  } = props;

  const [fieldRules, setFieldRules] = useState({});
  const [valueGot, setValueGot] = useState(null);

  useEffect(() => {
    setValueGot(props?.valueGot);
  }, [props?.valueGot]);

  useEffect(() => {
    valueGot != null &&
      valueGot != undefined &&
      valueGot != false &&
      // valueGot != "" &&
      props.setValue(fieldname, valueGot);
    if (valueGot == "empty") {
      props.setValue(fieldname, "");
    } else if (valueGot == 0) {
      props.setValue(fieldname, 0);
    }
  }, [valueGot]);

  useEffect(() => {
    if (props.disabled == true) {
      setFieldRules({});
      if (props?.register) props?.register(fieldname, { required: false });
      if (props?.resetField && !valueGot) props?.resetField(fieldname);
    } else {
      setFieldRules(rules);
    }
  }, [props.disabled]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        className={props.classes}
        tooltip={tooltip}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={fieldRules}
          render={({ field: { onChange, onBlur, ref, value } }) => (
            <AutoComplete
              key={valueGot}
              addonBefore={addonBefore}
              style={style}
              value={value}
              options={options}
              onSelect={(e, option) => {
                props.onSelect && props.onSelect(e, option);
              }}
              onSearch={(e) => {
                props.onSearch && props.onSearch(e);
              }}
              onClear={(e) => {
                props.onSelect && props.onSelect(e);
              }}
              filterOption={filterOption}
              ref={ref}
              allowClear
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              onBlur={props.onBlur}
              {...iProps}
              className="select-auto-dropdown"
              dropdownMatchSelectWidth={true}
            >
              {props?.children && props?.children}
            </AutoComplete>
          )}
        />
      </Form.Item>
    </>
  );
});

export const InputNumberField = (props) => {
  const {
    label,
    rules,
    iProps,
    control,
    validate,
    fieldname,
    initValue,
    isRequired,
    validMessage,
    formItemStyle,
    inputStyle,
  } = props;

  const [valueGot, setValueGot] = useState(null);
  const [fieldRules, setFieldRules] = useState({});

  useEffect(() => {
    setValueGot(props?.valueGot);
  }, [props?.valueGot]);

  useEffect(() => {
    valueGot != null && valueGot != undefined && valueGot != false && props.setValue(fieldname, valueGot);
    if (valueGot == "empty") {
      props.setValue(fieldname, "");
    }
  }, [valueGot]);

  useEffect(() => {
    if (props.disabled == true) {
      setFieldRules({});
      if (props?.register) props?.register(fieldname, { required: false });
      if (props?.resetField && !valueGot) props?.resetField(fieldname);
    } else {
      setFieldRules(rules);
    }
  }, [props.disabled]);

  return (
    <>
      <Form.Item
        label={label}
        help={validMessage}
        validateStatus={validate}
        className={props.classes}
        required={isRequired ? isRequired : false}
        style={formItemStyle}
      >
        <Controller
          name={fieldname}
          control={control}
          rules={fieldRules}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <InputNumber
              ref={ref}
              value={value ?? ""}
              onBlur={props.onBlur}
              disabled={props.disabled}
              style={inputStyle}
              onKeyDown={(e) => {
                const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab"];
                const isDigit = /^[0-9]$/.test(e.key);

                if (!isDigit && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                  return;
                }
              }}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const MaskInputField = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, isRequired, validate, validMessage } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        className={props.classes}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={rules}
          render={() => <MaskedInput {...iProps} />}
        />
      </Form.Item>
    </>
  );
};

export const InputPassword = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, isRequired, validate, validMessage } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item required={isRequired ? isRequired : false} label={label} validateStatus={validate} help={validMessage}>
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={rules}
          // render={(iProps)=><Input.Password {...iProps} />}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Input.Password
              value={props.disabled ? "" : value}
              ref={ref}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              onBlur={props.onBlur}
              {...iProps}
              disabled={props.disabled}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const SelectField = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, selectOption, isRequired, validate, validMessage } =
    props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      noStyle={props.noStyle}
    >
      <Controller
        name={fieldname}
        control={control}
        defaultValue={initValue ? initValue : ""}
        rules={rules}
        render={() => <Select {...iProps}>{selectOption}</Select>}
      />
    </Form.Item>
  );
};

export const ReactSelectCreateAble = (props) => {
  const [fieldRules, setFieldRules] = useState({});

  const {
    fieldname,
    isClearable,
    label,
    control,
    iProps,
    isRequired,
    validate,
    validMessage,
    rules,
    tooltip,
    options,
    onCreateOption,
    setValue,
    initalValue,
    onChange: customOnChange,
    setLessorInitial,
    setBenificiaryInitail,
    setBankNameInitial,
  } = props;

  useEffect(() => {
    if (iProps?.isDisabled) {
      setFieldRules({});
      return;
    }
    setFieldRules(rules || {});
  }, [iProps?.isDisabled, rules]);

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "32px",
      height: "41px", // Set custom height
      display: "flex",

      border: " 1px solid #d9d9d9",
      borderRadius: " 6px ",
    }),
    placeholder: (base) => ({
      ...base,
      marginBottom: "5px",
      height: "100%", // Match the height of the control
      display: "flex",
      alignItems: "center", // Vertically center placeholder
    }),
    input: (base) => ({
      ...base,
      margin: "0", // Ensure no extra spacing
    }),
  };

  const onChangeData = async (e, rhfOnChange) => {
    rhfOnChange(e);

    if (customOnChange) {
      customOnChange(e);
    } else {
      if (e && fieldname === "lessorName") {
        let translatedText = await translateService(e?.label);
        translatedText?.translatedText && setValue("lessorNameAr", translatedText?.translatedText);
        setLessorInitial(e);
      } else if (!e && fieldname === "lessorName") {
        setValue("lessorNameAr", null);
      } else if (fieldname === "beneficiaryName") {
        setBenificiaryInitail(e);
      } else if (fieldname === "bankName") {
        setBankNameInitial(e);
      }
    }
  };

  return (
    <>
      <Form.Item
        key={initalValue?.label}
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        className={props.classes}
        tooltip={tooltip}
      >
        <Controller
          name={fieldname}
          control={control}
          rules={fieldRules}
          render={({ field: { onChange, onBlur, ref, value } }) => (
            <CreatableSelect
              value={value ?? (initalValue?.value != undefined ? initalValue : null)}
              isClearable={isClearable}
              styles={customStyles}
              isDisabled={iProps?.isDisabled}
              options={options}
              onChange={(e) => onChangeData(e, onChange)}
              onCreateOption={(e) => {
                onCreateOption(e);
              }}
              onBlur={onBlur}
              {...iProps}
              theme={(theme) => ({
                ...theme,
                borderRadius: 2,
                colors: { ...theme.colors, primary: "#767676" },
              })}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const ReactSelectField = (props) => {
  const [fieldRules, setFieldRules] = useState({});
  const [paymentMode, setPaymentMode] = useState(null);
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    selectOption,
    isRequired,
    initValue,
    validate,
    valueGot,
    validMessage,
    style,
    tooltip,
    isMulti,
    placeholder,
    formatOptionLabel,
    components,
    isDisabled,
    menuPlacement,
    helpingText,
  } = props;

  const { styles: iStylesFromProps, ...restIProps } = iProps || {};

  const mergedStyles = useMemo(() => {
    const innerStyles = {
      control: (val, state) => ({
        ...val,
        borderRadius: 6,
        ...(validate === "error" && { borderColor: "#ff4d4f" }),
        ...(state.isFocused && {
          borderColor: validate === "error" ? "#ff4d4f" : val.borderColor,
          boxShadow: validate === "error" ? "0 0 0 2px rgba(255, 77, 79, 0.1)" : val.boxShadow,
        }),
        "&:hover": {
          borderColor: validate === "error" ? "#ff4d4f" : val.borderColor,
        },
        ...style,
      }),
      valueContainer: (vcontain) => ({
        ...vcontain,
        textTransform: "capitalize",
      }),
      dropdownIndicator: (icontain) => ({ ...icontain, padding: 5 }),
      indicatorSeparator: (icontain) => ({
        ...icontain,
        backgroundColor: "#fff",
      }),
      option: (vcontain) => ({
        ...vcontain,
        textTransform: "capitalize",
      }),
      placeholder: (place) => ({ ...place, color: "rgba(0,0,0,.3)" }),
      menuPortal: (base) => ({ ...base, zIndex: 99999999 }),
    };

    if (!iStylesFromProps || typeof iStylesFromProps !== "object") {
      return innerStyles;
    }

    const keys = new Set([...Object.keys(innerStyles), ...Object.keys(iStylesFromProps)]);
    const acc = {};
    keys.forEach((key) => {
      acc[key] = (base, state) => {
        let result = base;
        if (innerStyles[key]) result = innerStyles[key](result, state);
        if (iStylesFromProps[key]) result = iStylesFromProps[key](result, state);
        return result;
      };
    });
    return acc;
  }, [validate, style, iStylesFromProps]);

  useEffect(() => {
    valueGot && props.setValue(fieldname, valueGot);
  }, [valueGot]);

  useEffect(() => {
    if (props?.setMode) {
      ["cash", "cheque", "bank_transfer", "credit_card"]?.includes(paymentMode?.label) &&
        props?.setMode(paymentMode?.value);
    }
  }, [paymentMode, isRequired]);

  useEffect(() => {
    if (iProps?.isDisabled == true) {
      setFieldRules({});
      if (props?.register) props?.register(fieldname, { required: false });
      if (props?.resetField && !valueGot) props?.resetField(fieldname);
    } else {
      setFieldRules(rules);
    }
  }, [iProps?.isDisabled]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      className={props.classes}
      tooltip={tooltip}
    >
      <Controller
        name={fieldname}
        control={control}
        rules={fieldRules}
        defaultValue={initValue && initValue}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <RSelect
            ref={ref}
            value={value}
            isMulti={isMulti ? isMulti : false}
            isSearchable={true}
            isDisabled={isDisabled}
            menuPlacement={menuPlacement} // or "bottom"
            formatOptionLabel={formatOptionLabel}
            styles={mergedStyles}
            menuPortalTarget={document.body}
            onChange={(e) => {
              setPaymentMode(e);
              onChange(e);
              props.onChange && props.onChange(e);
            }}
            onBlur={props.onBlur}
            {...restIProps}
            options={selectOption}
            theme={(theme) => ({
              ...theme,
              borderRadius: 2,
              colors: { ...theme.colors, primary: "#767676" },
            })}
            components={components}
          />
        )}
      />
      {helpingText ? renderExtra(helpingText) : null}
    </Form.Item>
  );
};

export const DateField: React.FC<any> = React.memo((props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    tooltip,
    allowClear,
  } = props;

  const [fieldRules, setFieldRules] = useState({});

  useEffect(() => {
    props.valueGot && props.setValue && props.setValue(fieldname, props.valueGot);

    if (props.valueGot == "empty") {
      props.setValue(fieldname, "");
    }
  }, [props.valueGot]);

  useEffect(() => {
    if (props.disabled == true) {
      setFieldRules({});
      if (props?.register) props?.register(fieldname, { required: false });
      if (props?.resetField && !props.valueGot) props?.resetField(fieldname);
    } else {
      setFieldRules(rules);
    }
  }, [props.disabled]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        className={props.classes}
        tooltip={tooltip}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? initValue : ""}
          rules={fieldRules}
          render={({ field: { onChange, value, ref } }) => (
            <DatePicker
              allowClear={allowClear === false ? false : true}
              style={{ width: "100%" }}
              value={value}
              format={DATE_FORMATS.display}
              placeholder={DATE_FORMATS.display}
              ref={ref}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              {...iProps}
              disabled={props.disabled}
            />
          )}
        />
      </Form.Item>
    </>
  );
});

export const RangePickerField = (props) => {
  const { rules, label, iProps, control, validate, fieldname, initValue, isRequired, validMessage, disabled } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);

    if (props.valueGot == "empty") {
      props.setValue(fieldname, "");
    }
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        label={label}
        help={validMessage}
        required={isRequired}
        className={props.classes}
        validateStatus={validate}
      >
        <Controller
          rules={rules}
          name={fieldname}
          control={control}
          defaultValue={initValue}
          render={({ field: { onChange, value, ref } }) => (
            <RangePicker
              ref={ref}
              value={value}
              style={{ width: "100%" }}
              disabled={disabled}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              onCalendarChange={(e) => {
                props.onDateChange && props.onDateChange(e);
              }}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const TimeField = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, isRequired, validate, validMessage } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, []);

  return (
    <>
      <Form.Item required={isRequired ? isRequired : false} label={label} validateStatus={validate} help={validMessage}>
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? initValue : ""}
          rules={rules}
          render={() => <TimePicker style={{ width: "100%" }} {...iProps} />}
        />
      </Form.Item>
    </>
  );
};

export const TimeRangeField = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, isRequired, validate, validMessage } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item required={isRequired ? isRequired : false} label={label} validateStatus={validate} help={validMessage}>
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? initValue : ""}
          rules={rules}
          render={({ field: { onChange, value, ref } }) => (
            <TimePicker.RangePicker
              style={{ width: "100%" }}
              value={value}
              ref={ref}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const InputCheckbox = (props) => {
  const {
    fieldname,
    label,
    control,
    rules,
    initValue,
    isRequired,
    validate,
    iProps,
    validMessage,
    customeChange,
    checkedValue,
    style,
  } = props;

  useEffect(() => {
    props?.setValue && props?.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <Form.Item
      key={initValue}
      required={isRequired ? isRequired : false}
      validateStatus={validate}
      help={validMessage}
      valuePropName="checked"
      noStyle={style ? false : true}
    >
      <Controller
        name={fieldname}
        // key={props.valueGot }
        control={control}
        rules={rules}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { onChange, value } }) => (
          <Checkbox
            style={{ top: 0 }}
            checked={checkedValue ? props.valueGot : value}
            onChange={(e) => {
              customeChange && customeChange(e.target?.checked);
              onChange(e.target.checked);
            }}
            {...iProps}
          >
            {label}
          </Checkbox>
        )}
      />
    </Form.Item>
  );
};

export const InputSwitch = (props) => {
  const {
    fieldname,
    label,
    control,
    rules,
    initValue,
    isRequired,
    validate,
    iProps,
    validMessage,
    checkedChildren,
    unCheckedChildren,
    disabled,
  } = props;

  useEffect(() => {
    // if ((props?.valueGot == false || props?.valueGot == true) && )
    props?.setValue && props?.setValue(fieldname, props?.valueGot);
  }, [props?.valueGot]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      validateStatus={validate}
      help={validMessage}
      valuePropName="checked"
      noStyle
    >
      <Controller
        name={fieldname}
        control={control}
        rules={rules}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { onChange, value } }) => (
          <Switch
            className="ag-switch"
            checked={value}
            disabled={disabled}
            // size="small"
            // handleBg="#fff"
            onChange={(e) => {
              onChange(e);
            }}
            {...iProps}
            checkedChildren={checkedChildren || "Yes"}
            unCheckedChildren={unCheckedChildren || "No"}
          />
        )}
      />
    </Form.Item>
  );
};

export const UploadField = (props) => {
  const {
    fieldname,
    label,
    control,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    filelist,
    fileProps,
    iProps,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, []);

  return (
    <Form.Item required={isRequired ? isRequired : false} label={label} validateStatus={validate} help={validMessage}>
      <Controller
        name={fieldname}
        control={control}
        rules={rules}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { onChange, value } }) => (
          <Upload {...fileProps}>
            <Input
              {...iProps}
              value={filelist && filelist[0] && filelist[0].name ? filelist[0].name : "Choose a file to upload"}
              addonAfter="Browse"
              allow="csv"
            />
          </Upload>
        )}
      />
    </Form.Item>
  );
};

export const TextAreaField = (props) => {
  const { fieldname, label, control, iProps, rules, initValue, isRequired, validate, validMessage } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      className={props.classes}
    >
      <Controller
        name={fieldname}
        control={control}
        defaultValue={initValue || initValue == 0 ? initValue : ""}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Input.TextArea
            value={value ?? ""}
            ref={ref}
            onChange={(e) => {
              onChange(e);
              props.onChange && props.onChange(e);
            }}
            onBlur={props.onBlur}
            {...iProps}
            disabled={props.disabled}
          />
        )}
      />
    </Form.Item>
  );
};

export const InputRadio = (props) => {
  const {
    fieldname,
    label,
    control,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    noStyle,
    formItemStyle,
    disabled,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, []);
  return (
    <Form.Item
      label={label}
      noStyle={noStyle}
      style={formItemStyle}
      help={validMessage}
      valuePropName="checked"
      validateStatus={validate}
      required={isRequired ? isRequired : false}
      className={props.class ? props.class : ""}
    >
      <Controller
        rules={rules}
        name={fieldname}
        control={control}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { onChange, value } }) => (
          <Radio.Group
            value={value}
            disabled={disabled}
            onChange={(e) => {
              onChange(e.target.value);
              props.onChange && props.onChange(e);
            }}
          >
            {props.options}
          </Radio.Group>
        )}
      />
    </Form.Item>
  );
};

import { InternationalPhoneInput } from "./InternationalPhoneInput";
export { InternationalPhoneInput };
